import { usePage } from "@inertiajs/react";
import { XCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import DeploymentHistoryCard from "@components/website/deployment-history/DeploymentHistoryCard";
import type { Deployment } from "@components/website/deployment-history/DeploymentHistory.types";
import { DeployHistory } from "@components/deploy";
import { DeployPanel } from "@components/deploy/DeployPanel";
import { useDeployTasks } from "@api/deploys.hooks";
import deployImage from "@assets/deploy.png";

function buildDeployUrl(baseUrl: string | undefined): string | undefined {
  if (!baseUrl) return undefined;
  return baseUrl.startsWith("http") ? baseUrl : `https://${baseUrl}`;
}

const CAMPAIGN_TASK_NAMES: string[] = [
  "ConnectingGoogle",
  "VerifyingGoogle",
  "CheckingBilling",
  "DeployingCampaign",
  "EnablingCampaign",
];

function SuccessBanner({ hasCampaign }: { hasCampaign: boolean }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-6 px-10">
      <img src={deployImage} alt="Deployment" className="w-40 sm:w-56" />
      <div className="flex flex-col items-center gap-3 text-center">
        <h3 className="text-xl font-semibold text-base-500">
          {hasCampaign
            ? "You've just launched your first campaign"
            : "You've just launched your website"}
        </h3>
        <p className="max-w-md text-sm leading-[18px] text-base-300">
          Your big idea is now out in the world and attracting customers. This is just the
          beginning of something amazing!
        </p>
      </div>
    </div>
  );
}

function CancelledBanner() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-6 px-10">
      <div className="mx-auto w-14 h-14 rounded-full bg-neutral-100 flex items-center justify-center">
        <XCircleIcon className="size-7 text-base-400" />
      </div>
      <div className="flex flex-col items-center gap-2 text-center">
        <h3 className="text-xl font-semibold text-base-500">Deploy Cancelled</h3>
        <p className="max-w-md text-sm leading-[18px] text-base-300">
          This deployment was cancelled. You can start a new deploy when you're ready.
        </p>
      </div>
    </div>
  );
}

interface GoogleAdsErrorRow {
  message?: string;
  location?: string;
  error_code?: Record<string, string>;
  trigger?: string;
  policy_topics?: Array<{ topic: string; type: string }>;
  policy?: { external_policy_name?: string; exemptible?: boolean };
  // Friendly description of a known policy topic. When present, the
  // backend has stripped `message` and `location` because those fields
  // are scary internal API noise on policy disapprovals — we render
  // policy_summary as the only line. When absent (either no policy
  // topic at all, or a topic we don't yet have a mapping for), fall
  // back to the original message + location.
  policy_summary?: string;
  // Friendly description of a known field error (e.g. missing domain).
  // Comes from FriendlyGoogleErrors::FIELD_ERRORS — backend strips
  // message/location when this is set.
  friendly_summary?: string;
  // Optional CTA tied to friendly_summary. The href may contain
  // `:project_uuid`, which we interpolate from page props.
  action?: { label: string; href: string };
}

function interpolateHref(href: string, projectUuid?: string): string {
  if (!projectUuid) return href;
  return href.replace(":project_uuid", projectUuid);
}

function GoogleAdsErrorList({
  errors,
  projectUuid,
}: {
  errors: GoogleAdsErrorRow[];
  projectUuid?: string;
}) {
  return (
    <ul className="mt-4 flex max-w-xl flex-col gap-3 text-left text-sm text-base-400">
      {errors.map((err, i) => (
        <li
          key={i}
          data-testid="google-ads-error-row"
          className="rounded-md border border-red-100 bg-red-50/40 p-3"
        >
          {err.friendly_summary ? (
            <>
              <p className="font-medium text-base-500">{err.friendly_summary}</p>
              {err.action && (
                <a
                  href={interpolateHref(err.action.href, projectUuid)}
                  className="mt-2 inline-block text-sm font-medium text-blue-600 hover:underline"
                >
                  {err.action.label} →
                </a>
              )}
            </>
          ) : err.policy_summary ? (
            <>
              <p className="text-base-500">
                Google disapproved your ad due to the following reason:
              </p>
              <p className="mt-1 font-medium text-base-500">{err.policy_summary}</p>
            </>
          ) : (
            <>
              {err.message && <p className="font-medium text-base-500">{err.message}</p>}
              {err.policy_topics && err.policy_topics.length > 0 && (
                <p className="mt-1 text-xs text-base-300">
                  Policy:{" "}
                  {err.policy_topics
                    .map((t) => `${t.topic} (${t.type})`)
                    .join(", ")}
                </p>
              )}
              {err.location && (
                <p className="mt-1 font-mono text-xs text-base-300">{err.location}</p>
              )}
            </>
          )}
          {err.policy?.exemptible === true && (
            <p className="mt-1 text-xs text-base-300">This policy may be exemptible.</p>
          )}
        </li>
      ))}
    </ul>
  );
}

function FailedBanner({
  errorMessage,
  googleAdsErrors,
  projectUuid,
}: {
  errorMessage?: string;
  googleAdsErrors?: GoogleAdsErrorRow[];
  projectUuid?: string;
}) {
  const hasStructuredErrors = googleAdsErrors && googleAdsErrors.length > 0;

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-6 px-10">
      <div className="mx-auto w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
        <ExclamationTriangleIcon className="size-7 text-red-600" />
      </div>
      <div className="flex flex-col items-center gap-2 text-center">
        <h3 className="text-xl font-semibold text-base-500">Deployment Failed</h3>
        {!hasStructuredErrors && (
          <p className="max-w-md text-sm leading-[18px] text-base-300">
            {errorMessage || "Something went wrong during deployment."}{" "}
            Please try again or contact support if the issue persists.
          </p>
        )}
      </div>
      {hasStructuredErrors && (
        <GoogleAdsErrorList errors={googleAdsErrors} projectUuid={projectUuid} />
      )}
    </div>
  );
}

export default function DeployCompleteScreen({ actions }: { actions?: React.ReactNode }) {
  const { website_url, deploy, project } = usePage<{
    website_url?: string;
    deploy?: { id: number; status: string; tasks: any[] };
    project?: { uuid?: string };
  }>().props;

  const { data } = useDeployTasks(deploy?.id, deploy?.tasks, deploy?.status);
  const tasks = data?.tasks ?? [];
  const deployStatus = data?.deploy_status ?? deploy?.status;

  const hasCampaign = tasks.some((t) => CAMPAIGN_TASK_NAMES.includes(t.name));
  const deployUrl = buildDeployUrl(website_url || undefined);
  const isSuccess = deployStatus === "completed";
  const isCancelled = deployStatus === "cancelled";

  const failedTask = tasks.find((t) => t.status === "failed");

  const deployment: Deployment = {
    id: "current",
    status: "success",
    isNew: true,
    isLive: true,
    timestamp: new Date().toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }),
    url: deployUrl,
  };

  return (
    <DeployPanel.Root>
      <DeployPanel.Header>
        <DeployPanel.Title
          description={
            <>
              This page tracks all deployments for your landing page
              {hasCampaign ? " and ad campaigns" : ""}. Review the status and details of each
              deployment to ensure optimal performance and quickly identify any issues.
            </>
          }
        >
          Deployment History
        </DeployPanel.Title>
        {actions && <DeployPanel.Actions>{actions}</DeployPanel.Actions>}
      </DeployPanel.Header>

      <DeployPanel.Body className="py-12">
        {isSuccess && <SuccessBanner hasCampaign={hasCampaign} />}
        {isCancelled && <CancelledBanner />}
        {!isSuccess && !isCancelled && (
          <FailedBanner
            errorMessage={failedTask?.error_message ?? undefined}
            googleAdsErrors={
              (failedTask?.result_data as { google_ads_errors?: GoogleAdsErrorRow[] } | null)
                ?.google_ads_errors
            }
            projectUuid={project?.uuid}
          />
        )}

        {isSuccess && (
          <div className="px-10 py-4">
            <DeploymentHistoryCard deployment={deployment} />
          </div>
        )}
        <DeployHistory />
      </DeployPanel.Body>
    </DeployPanel.Root>
  );
}
